import * as React from 'react'
import { Tooltip as TooltipPrimitive } from 'radix-ui'

import { useTabOwnerId } from '@/components/layout/SheetContainerContext'
import { cn } from '@/lib/utils'

function TooltipProvider({
  delayDuration = 80,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  )
}

function Tooltip({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />
}

function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
}

function TooltipContent({
  className,
  sideOffset = 0,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  // TooltipContent portals to document.body, OUTSIDE the per-tab <Activity>
  // trees — so a tooltip whose tab is hidden mid-exit-animation never gets its
  // animationend and would stay stranded over the newly active tab (anchored
  // to a zero-rect trigger at the page's top-left corner). Stamp the owning
  // tab so AuthLayout's [data-tab-owner] CSS rule hides it without touching
  // the frozen subtree — same lever as sheet.tsx / popover.tsx / dialog.tsx
  // (see SheetContainerContext). Null outside a tab → no stamp, unchanged
  // behavior.
  const ownerTabId = useTabOwnerId()
  const ownerProps = ownerTabId ? { 'data-tab-owner': ownerTabId } : {}
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        {...ownerProps}
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          'z-50 w-fit origin-(--radix-tooltip-content-transform-origin) animate-in duration-quick ease-out rounded-md bg-foreground px-3 py-1.5 text-xs text-balance text-background fade-in-0 zoom-in-[0.98] data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-[0.98] data-[state=instant-open]:animate-none',
          className
        )}
        {...props}>
        {children}
        <TooltipPrimitive.Arrow className="z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px] bg-foreground fill-foreground" />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
